import type {
	AssignEmployeesBody,
	AssignEmployeesResponseJSON,
	CheckRoleNameResponse,
	CreateRoleBody,
	ListRoleEmployeesQuery,
	ListRoleOptionsResponseJSON,
	ListRolesResponseJSON,
	PatchRoleCompetencyBody,
	RoleCompetenciesResponseJSON,
	RoleEmployeesResponseJSON,
	SingleRoleResponseJSON,
	UpdateRoleBody,
} from "@performyard/service-contracts";

import request from "./internal-api/internal-base-request";

export type RoleSortBy = "name" | "description" | "status" | "employees_count" | "competencies_count";

export interface ListRolesParams {
	name?: string;
	status?: "active" | "archived" | "any";
	sortBy?: RoleSortBy;
	sortOrder?: "asc" | "desc";
	limit?: number;
	skip?: number;
}

export type ListRoleOptionsParams = Pick<ListRolesParams, "name" | "limit" | "skip"> & { ids?: string[] };

// GET /v1/roles
export const getRoles = async (params: ListRolesParams = {}): Promise<ListRolesResponseJSON> => {
	const searchParams = new URLSearchParams();
	if (params.name) searchParams.set("name", params.name);
	if (params.status) searchParams.set("status", params.status);
	if (params.sortBy) searchParams.set("sortBy", params.sortBy);
	if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
	if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
	if (params.skip !== undefined) searchParams.set("skip", String(params.skip));

	const query = searchParams.toString();
	const [response] = await request<ListRolesResponseJSON>(`/v1/roles${query ? `?${query}` : ""}`, {
		method: "GET",
	});
	return response;
};

// GET /v1/roles/options
export const getRoleOptions = async (params: ListRoleOptionsParams = {}): Promise<ListRoleOptionsResponseJSON> => {
	const searchParams = new URLSearchParams();
	if (params.name) searchParams.set("name", params.name);
	if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
	if (params.skip !== undefined) searchParams.set("skip", String(params.skip));
	if (params.ids?.length) searchParams.set("ids", params.ids.join(","));

	const query = searchParams.toString();
	const [response] = await request<ListRoleOptionsResponseJSON>(`/v1/roles/options${query ? `?${query}` : ""}`, {
		method: "GET",
	});
	return response;
};

// PATCH /v1/roles/:id/archive
export const archiveRole = async (id: string): Promise<{ success: boolean }> => {
	const [response] = await request<{ success: boolean }>(`/v1/roles/${id}/archive`, {
		method: "PATCH",
	});
	return response;
};

// PATCH /v1/roles/:id/reopen
export const reopenRole = async (id: string): Promise<{ success: boolean }> => {
	const [response] = await request<{ success: boolean }>(`/v1/roles/${id}/reopen`, {
		method: "PATCH",
	});
	return response;
};

// GET /v1/roles/:id
export const getRole = async (id: string): Promise<SingleRoleResponseJSON> => {
	const [response] = await request<SingleRoleResponseJSON>(`/v1/roles/${id}`, {
		method: "GET",
	});
	return response;
};

// POST /v1/roles
export const createRole = async (body: CreateRoleBody): Promise<SingleRoleResponseJSON> => {
	const [response] = await request<SingleRoleResponseJSON>("/v1/roles", {
		method: "POST",
		body: JSON.stringify(body),
	});
	return response;
};

// PUT /v1/roles/:id
export const updateRole = async (id: string, body: UpdateRoleBody): Promise<SingleRoleResponseJSON> => {
	const [response] = await request<SingleRoleResponseJSON>(`/v1/roles/${id}`, {
		method: "PUT",
		body: JSON.stringify(body),
	});
	return response;
};

// GET /v1/roles/check-name
export const checkRoleName = async (name: string, excludeId?: string): Promise<CheckRoleNameResponse> => {
	const searchParams = new URLSearchParams({ name });
	if (excludeId) searchParams.set("excludeId", excludeId);

	const [response] = await request<CheckRoleNameResponse>(`/v1/roles/check-name?${searchParams.toString()}`, {
		method: "GET",
	});
	return response;
};

// GET /v1/roles/:id/competencies
export const getRoleCompetencies = async (roleId: string): Promise<RoleCompetenciesResponseJSON> => {
	const [response] = await request<RoleCompetenciesResponseJSON>(`/v1/roles/${roleId}/competencies`, {
		method: "GET",
	});
	return response;
};

// PATCH /v1/roles/:id/competencies — backend returns the updated role doc so callers can
// write it straight into the detail cache (see `usePatchRoleCompetency`).
export const patchRoleCompetency = async (
	roleId: string,
	body: PatchRoleCompetencyBody
): Promise<SingleRoleResponseJSON> => {
	const [response] = await request<SingleRoleResponseJSON>(`/v1/roles/${roleId}/competencies`, {
		method: "PATCH",
		body: JSON.stringify(body),
	});
	return response;
};

export type ListRoleEmployeesParams = Partial<ListRoleEmployeesQuery>;

// GET /v1/roles/:id/employees
export const getRoleEmployees = async (
	roleId: string,
	params: ListRoleEmployeesParams = {}
): Promise<RoleEmployeesResponseJSON> => {
	const searchParams = new URLSearchParams();
	if (params.search) searchParams.set("search", params.search);
	if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
	if (params.skip !== undefined) searchParams.set("skip", String(params.skip));
	const query = searchParams.toString();
	const [response] = await request<RoleEmployeesResponseJSON>(
		`/v1/roles/${roleId}/employees${query ? `?${query}` : ""}`,
		{ method: "GET" }
	);
	return response;
};

// POST /v1/roles/:id/employees
export const assignRoleEmployees = async (
	roleId: string,
	body: AssignEmployeesBody
): Promise<AssignEmployeesResponseJSON> => {
	const [response] = await request<AssignEmployeesResponseJSON>(`/v1/roles/${roleId}/employees`, {
		method: "POST",
		body: JSON.stringify(body),
	});
	return response;
};

// DELETE /v1/roles/:id/employees/:user_id
export const removeRoleEmployee = async (roleId: string, userId: string): Promise<void> => {
	await request(`/v1/roles/${roleId}/employees/${userId}`, { method: "DELETE" });
};
